import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import moment from 'moment';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Skeleton from '@material-ui/lab/Skeleton';
import Snackbar from '@material-ui/core/Snackbar';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/EditOutlined';
import { useTranslation } from 'react-i18next';
import { Column } from 'components/UI/Column';
import { Row } from 'components/UI/Row';
import {
  closeError,
  getPasswordSaved,
  getProfile,
  getProfileErrors,
  getProfileLoaders,
  fetchProfile,
  updatePassword,
  resetProfile,
  ProfilePasswordUpdate
} from './profiles';
import { ProfileEdit } from './ProfileEdit';

const UpdateProfilePasswordSchema = object().shape({
  newPassword: string()
    .min(8, 'errors.password is too short')
    .required('errors.password is required'),
  newPasswordCompare: string()
    .min(8, 'errors.password is too short')
    .required('errors.password is required')
    .test(
      'password',
      'errors.password must be confirmed',
      /* eslint-disable func-names */ function(passwordCompare) {
        return passwordCompare === this.parent.newPassword;
      }
    ),
  oldPassword: string()
    .min(8, 'errors.password is too short')
    .required('errors.password is required')
});

const Profile: FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const profile = useSelector(getProfile);
  const { isLoading, isSaving } = useSelector(getProfileLoaders);
  const { loadingError, savingError } = useSelector(getProfileErrors);
  const passwordSaved = useSelector(getPasswordSaved);
  const [editProfile, setEditProfile] = useState(false);

  const { handleSubmit, errors, control, reset } = useForm<ProfilePasswordUpdate>({
    mode: 'onBlur',
    resolver: yupResolver(UpdateProfilePasswordSchema)
  });

  useEffect(() => {
    dispatch(fetchProfile(null));
    return () => {
      dispatch(resetProfile());
    };
  }, []);

  useEffect(() => {
    if (savingError || passwordSaved) {
      reset();
    }
  }, [savingError, passwordSaved]);

  const onSubmit = async (formData: ProfilePasswordUpdate) => {
    dispatch(updatePassword(formData));
    // manage errors
  };

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  return (
    <Row>
      <Column style={{ marginRight: '10px' }}>
        <Row>
          <h3>{t('account.title')} </h3>
          {!editProfile && (
            <div style={{ position: 'relative' }}>
              <IconButton
                aria-label="edit"
                color="primary"
                onClick={() => {
                  setEditProfile(true);
                }}
                style={{ position: 'absolute', top: '5px' }}
              >
                <EditIcon />
              </IconButton>
            </div>
          )}
        </Row>
        <>
          <Row alignItems="center" flex="0 0 50px">
            {!editProfile && <b style={{ marginRight: '10px' }}>{t('label.name')}</b>}
            {isLoading ? (
              <Skeleton variant="text" animation="wave" width="50%" />
            ) : (
              <>
                {editProfile ? (
                  <ProfileEdit setEditProfile={setEditProfile} />
                ) : (
                  <>
                    {profile?.first_name} {profile?.last_name}
                  </>
                )}
              </>
            )}
          </Row>
        </>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('account.company_name')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.company_name || '-'}</>}
        </Row>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('label.tax_code')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.tax_code}</>}
        </Row>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('label.address')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.address}</>}
        </Row>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('label.city')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.city}</>}
        </Row>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('label.zip code')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.postal_code}</>}
        </Row>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('label.phone number')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.phone_number}</>}
        </Row>

        <Row alignItems="center" flex="0 0 50px">
          <b style={{ marginRight: '10px' }}>{t('label.email')}</b>
          {isLoading ? <Skeleton variant="text" animation="wave" width="50%" /> : <>{profile?.email}</>}
        </Row>

        {profile?.license_expiry_date && (
          <Row alignItems="center" flex="0 0 50px">
            <b style={{ marginRight: '10px' }}>{t('account.license expiry date')}</b>{' '}
            {isLoading ? (
              <Skeleton variant="text" animation="wave" width="50%" />
            ) : (
              <>{moment(profile?.license_expiry_date).format('YYYY-MM-DD')}</>
            )}
          </Row>
        )}
      </Column>
      <Column alignItems="flex-start" style={{ marginLeft: '10px' }}>
        {!isLoading && (
          <>
            <h3>{t('account.change password')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Column alignItems="flex-end">
                <Controller
                  as={
                    <TextField
                      autoComplete="new-password"
                      helperText={t(get(errors, 'oldPassword.message'))}
                      error={!isEmpty(get(errors, `oldPassword`))}
                      label={t('account.old password')}
                      type="password"
                      style={{ marginBottom: '16px', width: '400px' }}
                      variant="outlined"
                    />
                  }
                  control={control}
                  defaultValue=""
                  name="oldPassword"
                />
                <Controller
                  as={
                    <TextField
                      autoComplete="new-password"
                      helperText={t(get(errors, 'newPassword.message'))}
                      error={!isEmpty(get(errors, `newPassword`))}
                      label={t('account.new password')}
                      type="password"
                      style={{ marginBottom: '16px', width: '400px' }}
                      variant="outlined"
                    />
                  }
                  control={control}
                  defaultValue=""
                  name="newPassword"
                />
                <Controller
                  as={
                    <TextField
                      autoComplete="new-password"
                      helperText={t(get(errors, 'newPasswordCompare.message'))}
                      error={!isEmpty(get(errors, `newPasswordCompare`))}
                      label={t('account.confirm new password')}
                      type="password"
                      style={{ marginBottom: '16px', width: '400px' }}
                      variant="outlined"
                    />
                  }
                  control={control}
                  defaultValue=""
                  name="newPasswordCompare"
                />
                <Button type="submit" color="primary" disabled={isSaving}>
                  {t('label.save')}
                </Button>
              </Column>
            </form>
          </>
        )}
      </Column>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(savingError)}
        autoHideDuration={5000}
        onClose={() => dispatch(closeError())}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {savingError}
        </Alert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={passwordSaved}
        autoHideDuration={5000}
        onClose={() => dispatch(closeError())}
      >
        <Alert severity="success" variant="filled" onClose={() => dispatch(closeError())}>
          {t('account.password updated')}
        </Alert>
      </Snackbar>
    </Row>
  );
};

export { Profile };
